/**
 * Modal para asignar roles a usuarios
 * - admin_union puede asignar: admin_union, admin_association, club_director
 * - admin_association puede asignar: club_director (flujo simplificado de 2 pasos)
 *
 * Mejoras:
 * - Búsqueda y filtro por tipo en la selección de club
 * - Muestra director actual de cada club
 * - Iglesias colapsables con conteo de clubes sin director
 * - Modal más ancho para escalabilidad (200+ clubes)
 */

import { useState, useMemo, useEffect } from "react";
import {
  X,
  Shield,
  User,
  Building2,
  Users,
  ChevronRight,
  ChevronDown,
  Loader2,
  Crown,
  Briefcase,
  Church,
  Search,
  AlertCircle,
} from "lucide-react";
import { Button, Badge, SectionBadge } from "@/components/ui";
import { useSystemUsers } from "@/hooks/useUsers";
import { useUnions, useAssociations } from "@/hooks/useOrganization";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { useAssignRole, useRoleAssignments } from "@/hooks/useRoles";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import {
  SYSTEM_ROLE_LABELS,
  type SystemRole,
  type SystemUser,
  type ClubType,
} from "@/types";
import { toast } from "react-hot-toast";
import { cn, getActiveSections } from "@/lib/utils";

interface AssignRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = "user" | "role" | "scope";

export default function AssignRoleModal({
  isOpen,
  onClose,
}: AssignRoleModalProps) {
  const { profile } = useAuthStore();
  const { isUnionAdmin, isAssociationAdmin } = usePermissions();

  const { data: users = [] } = useSystemUsers();
  const { data: unions = [] } = useUnions();
  const { data: associations = [] } = useAssociations();
  const { data: clubs = [] } = useAccessibleClubs();
  const { data: existingAssignments = [] } = useRoleAssignments();

  const assignRoleMutation = useAssignRole();

  const [step, setStep] = useState<Step>("user");
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<SystemRole | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [clubSearchQuery, setClubSearchQuery] = useState("");
  const [clubTypeFilter, setClubTypeFilter] = useState<string>("all");
  const [expandedChurches, setExpandedChurches] = useState<Set<string>>(
    new Set(["__all__"]),
  );

  // Para admin_association: solo pueden asignar club_director → flujo de 2 pasos
  const isSimplifiedFlow = isAssociationAdmin && !isUnionAdmin;

  const visibleSteps: Step[] = isSimplifiedFlow
    ? ["user", "scope"]
    : ["user", "role", "scope"];

  const currentStepIndex = visibleSteps.indexOf(step);

  // Reset cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      setStep("user");
      setSelectedUser(null);
      setSelectedRole(null);
      setUserSearchQuery("");
      setClubSearchQuery("");
      setClubTypeFilter("all");
      setExpandedChurches(new Set(["__all__"]));
    }
  }, [isOpen]);

  // Usuarios activos
  const activeUsers = useMemo(() => {
    return users.filter((u) => u.status === "active");
  }, [users]);

  // Filtrar usuarios
  const filteredUsers = useMemo(() => {
    if (!userSearchQuery) return activeUsers;
    const query = userSearchQuery.toLowerCase();
    return activeUsers.filter(
      (u) =>
        u.displayName.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query),
    );
  }, [activeUsers, userSearchQuery]);

  // Roles disponibles según permisos
  const availableRoles = useMemo(() => {
    const roles: SystemRole[] = [];
    if (isUnionAdmin) {
      roles.push("admin_union", "admin_association");
    }
    if (isUnionAdmin || isAssociationAdmin) {
      roles.push("club_director");
    }
    return roles;
  }, [isUnionAdmin, isAssociationAdmin]);

  // Mapa de director actual por clubId
  const directorsByClub = useMemo(() => {
    const map = new Map<string, { userId: string; name: string }>();
    if (!existingAssignments.length) return map;
    existingAssignments
      .filter((a) => a.role === "club_director" && a.status === "active")
      .forEach((a) => {
        const user = users.find((u) => u.id === a.userId);
        map.set(a.scopeId, {
          userId: a.userId,
          name: user?.displayName || "Director asignado",
        });
      });
    return map;
  }, [existingAssignments, users]);

  const effectiveRole = isSimplifiedFlow ? "club_director" : selectedRole;

  // Scope options
  const getScopeOptions = () => {
    switch (effectiveRole) {
      case "admin_union":
        return unions.map((u) => ({
          id: u.id,
          name: u.name,
          type: "union" as const,
        }));
      case "admin_association":
        return associations.map((a) => ({
          id: a.id,
          name: a.name,
          type: "association" as const,
        }));
      case "club_director":
        return clubs.map((c) => ({
          id: c.id,
          name: c.name,
          type: "club" as const,
          churchId: c.churchId,
          churchName: c.churchName || "Sin iglesia",
          clubType: c.type,
          clubSections: getActiveSections(c),
        }));
      default:
        return [];
    }
  };

  const scopeOptions = getScopeOptions();

  // Clubes agrupados por iglesia con búsqueda y filtro
  const clubsGroupedByChurch = useMemo(() => {
    if (effectiveRole !== "club_director") return [];

    const clubOptions = scopeOptions as Array<{
      id: string;
      name: string;
      type: "club";
      churchId: string;
      churchName: string;
      clubType: ClubType;
      clubSections: string[];
    }>;

    const searchLower = clubSearchQuery.toLowerCase();
    const searched = searchLower
      ? clubOptions.filter(
          (c) =>
            c.name.toLowerCase().includes(searchLower) ||
            c.churchName.toLowerCase().includes(searchLower),
        )
      : clubOptions;

    const filtered =
      clubTypeFilter === "all"
        ? searched
        : searched.filter((c) => c.clubSections.includes(clubTypeFilter));

    const grouped = new Map<
      string,
      { churchName: string; clubs: typeof filtered; withoutDirector: number }
    >();

    filtered.forEach((club) => {
      const existing = grouped.get(club.churchId);
      const hasDirector = directorsByClub.has(club.id);
      if (existing) {
        existing.clubs.push(club);
        if (!hasDirector) existing.withoutDirector++;
      } else {
        grouped.set(club.churchId, {
          churchName: club.churchName,
          clubs: [club],
          withoutDirector: hasDirector ? 0 : 1,
        });
      }
    });

    return Array.from(grouped.entries())
      .sort(([, a], [, b]) => a.churchName.localeCompare(b.churchName))
      .map(([churchId, data]) => ({
        churchId,
        churchName: data.churchName,
        clubs: data.clubs.sort((a, b) => a.name.localeCompare(b.name)),
        withoutDirector: data.withoutDirector,
      }));
  }, [
    scopeOptions,
    effectiveRole,
    clubSearchQuery,
    clubTypeFilter,
    directorsByClub,
  ]);

  const hasExistingRole = (
    userId: string,
    role: SystemRole,
    scopeId: string,
  ): boolean => {
    return existingAssignments.some(
      (a) =>
        a.userId === userId &&
        a.role === role &&
        a.scopeId === scopeId &&
        a.status === "active",
    );
  };

  const clubsWithoutDirectorCount = useMemo(() => {
    if (effectiveRole !== "club_director") return 0;
    return clubs.filter((c) => !directorsByClub.has(c.id)).length;
  }, [clubs, directorsByClub, effectiveRole]);

  const handleSelectUser = (user: SystemUser) => {
    setSelectedUser(user);
    if (isSimplifiedFlow) {
      setSelectedRole("club_director");
      setStep("scope");
    } else {
      setStep("role");
    }
  };

  const handleSelectRole = (role: SystemRole) => {
    setSelectedRole(role);
    setStep("scope");
  };

  const handleSelectScope = async (
    scopeId: string,
    scopeName: string,
    scopeType: "union" | "association" | "club",
  ) => {
    if (!selectedUser || !effectiveRole) return;
    if (hasExistingRole(selectedUser.id, effectiveRole, scopeId)) {
      toast.error("Este usuario ya tiene este rol asignado");
      return;
    }
    try {
      await assignRoleMutation.mutateAsync({
        userId: selectedUser.id,
        role: effectiveRole,
        scopeType,
        scopeId,
        scopeName,
        assignedBy: profile?.email || "system",
      });
      toast.success(
        `Rol de ${SYSTEM_ROLE_LABELS[effectiveRole]} asignado a ${selectedUser.displayName}`,
      );
      onClose();
    } catch (error) {
      console.error("Error assigning role:", error);
      toast.error("Error al asignar el rol");
    }
  };

  const goBack = () => {
    if (step === "scope") {
      if (isSimplifiedFlow) {
        setSelectedRole(null);
        setSelectedUser(null);
        setClubSearchQuery("");
        setClubTypeFilter("all");
        setStep("user");
      } else {
        setSelectedRole(null);
        setClubSearchQuery("");
        setClubTypeFilter("all");
        setStep("role");
      }
    } else if (step === "role") {
      setSelectedUser(null);
      setStep("user");
    }
  };

  const toggleChurch = (churchId: string) => {
    setExpandedChurches((prev) => {
      const next = new Set(prev);
      if (next.has(churchId)) next.delete(churchId);
      else next.add(churchId);
      return next;
    });
  };

  if (!isOpen) return null;

  const getRoleIcon = (role: SystemRole) => {
    switch (role) {
      case "admin_union":
        return <Crown className="w-5 h-5" />;
      case "admin_association":
        return <Briefcase className="w-5 h-5" />;
      case "club_director":
        return <Users className="w-5 h-5" />;
      default:
        return <Shield className="w-5 h-5" />;
    }
  };

  const getRoleColor = (role: SystemRole) => {
    switch (role) {
      case "admin_union":
        return "text-purple-600 bg-purple-100";
      case "admin_association":
        return "text-blue-600 bg-blue-100";
      case "club_director":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/25">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isSimplifiedFlow ? "Asignar Director" : "Asignar Rol"}
              </h2>
              {isSimplifiedFlow && (
                <p className="text-xs text-gray-500">
                  Selecciona un usuario y el club que dirigirá
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-4 p-4 border-b border-gray-100">
          {visibleSteps.map((s, i) => {
            const stepLabels: Record<Step, string> = {
              user: "Usuario",
              role: "Rol",
              scope: isSimplifiedFlow
                ? "Club"
                : effectiveRole === "club_director"
                  ? "Club"
                  : "Ámbito",
            };
            const isActive = step === s;
            const isPast = currentStepIndex > i;
            return (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <ChevronRight className="w-4 h-4 text-gray-300" />}
                <div
                  className={cn(
                    "flex items-center gap-2",
                    isActive ? "text-primary-600 font-medium" : "text-gray-400",
                  )}
                >
                  <div
                    className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center text-sm",
                      isActive
                        ? "bg-primary-100"
                        : isPast
                          ? "bg-green-100 text-green-600"
                          : "bg-gray-100",
                    )}
                  >
                    {i + 1}
                  </div>
                  <span className="text-sm">{stepLabels[s]}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Step: Select User */}
          {step === "user" && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar usuario por nombre o email..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
                  autoFocus
                />
              </div>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">
                    No hay usuarios disponibles
                  </p>
                ) : (
                  filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200 text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-md shadow-primary-500/25">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {user.displayName}
                        </p>
                        <p className="text-sm font-medium text-gray-500 truncate">
                          {user.email}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step: Select Role (only for admin_union) */}
          {step === "role" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Para:</span>
                <span className="font-medium text-gray-900">
                  {selectedUser?.displayName}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Selecciona el rol a asignar:
              </p>
              <div className="space-y-2">
                {availableRoles.map((role) => (
                  <button
                    key={role}
                    onClick={() => handleSelectRole(role)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200 text-left"
                  >
                    <div
                      className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center",
                        getRoleColor(role),
                      )}
                    >
                      {getRoleIcon(role)}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {SYSTEM_ROLE_LABELS[role]}
                      </p>
                      <p className="text-sm font-medium text-gray-500">
                        {role === "admin_union" && "Administra toda la unión"}
                        {role === "admin_association" &&
                          "Administra una asociación"}
                        {role === "club_director" && "Dirige un club"}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step: Select Scope */}
          {step === "scope" && (
            <div className="space-y-4">
              {/* Context bar */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl flex-wrap">
                <User className="w-4 h-4 text-gray-500 shrink-0" />
                <span className="font-medium text-gray-900">
                  {selectedUser?.displayName}
                </span>
                {!isSimplifiedFlow && effectiveRole && (
                  <>
                    <span className="text-gray-400">→</span>
                    <Badge className={getRoleColor(effectiveRole)}>
                      {SYSTEM_ROLE_LABELS[effectiveRole]}
                    </Badge>
                  </>
                )}
              </div>

              {/* Club selection — enhanced for 200+ clubs */}
              {effectiveRole === "club_director" ? (
                <div className="space-y-3">
                  {/* Search + type filter */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Buscar club o iglesia..."
                        value={clubSearchQuery}
                        onChange={(e) => setClubSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
                        autoFocus
                      />
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {(
                        [
                          ["all", "Todos"],
                          ["aventureros", "Aventureros"],
                          ["conquistadores", "Conquistadores"],
                          ["guias_mayores", "Guías Mayores"],
                        ] as const
                      ).map(([value, label]) => (
                        <button
                          key={value}
                          onClick={() => setClubTypeFilter(value)}
                          className={cn(
                            "px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap",
                            clubTypeFilter === value
                              ? "bg-primary-500 text-white shadow-md shadow-primary-500/25"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Warning: clubs without director */}
                  {clubsWithoutDirectorCount > 0 && (
                    <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                      <p className="text-xs text-amber-800">
                        <span className="font-semibold">
                          {clubsWithoutDirectorCount}
                        </span>{" "}
                        {clubsWithoutDirectorCount === 1 ? "club" : "clubes"}{" "}
                        sin director asignado
                      </p>
                    </div>
                  )}

                  {/* Grouped clubs by church */}
                  <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                    {clubsGroupedByChurch.length === 0 ? (
                      <div className="text-center py-6">
                        <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">
                          No se encontraron clubes
                        </p>
                      </div>
                    ) : (
                      clubsGroupedByChurch.map((church) => (
                        <div
                          key={church.churchId}
                          className="border border-gray-100 rounded-xl overflow-hidden"
                        >
                          {/* Church header */}
                          <button
                            onClick={() => toggleChurch(church.churchId)}
                            className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-white hover:from-blue-100 hover:to-blue-50/50 transition-colors duration-200"
                          >
                            <div className="flex items-center gap-2">
                              {expandedChurches.has(church.churchId) ? (
                                <ChevronDown className="w-4 h-4 text-blue-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-blue-500" />
                              )}
                              <Church className="w-4 h-4 text-blue-600" />
                              <span className="font-semibold text-sm text-blue-900">
                                {church.churchName}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {church.withoutDirector > 0 && (
                                <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                                  {church.withoutDirector} sin director
                                </Badge>
                              )}
                              <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                                {church.clubs.length}
                              </Badge>
                            </div>
                          </button>

                          {/* Clubs */}
                          {expandedChurches.has(church.churchId) && (
                            <div className="p-2 space-y-1.5">
                              {church.clubs.map((club) => {
                                const alreadyAssigned =
                                  selectedUser &&
                                  hasExistingRole(
                                    selectedUser.id,
                                    "club_director",
                                    club.id,
                                  );
                                const currentDirector = directorsByClub.get(
                                  club.id,
                                );
                                return (
                                  <button
                                    key={club.id}
                                    onClick={() =>
                                      !alreadyAssigned &&
                                      handleSelectScope(
                                        club.id,
                                        club.name,
                                        "club",
                                      )
                                    }
                                    disabled={
                                      !!alreadyAssigned ||
                                      assignRoleMutation.isPending
                                    }
                                    className={cn(
                                      "w-full flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 text-left",
                                      alreadyAssigned
                                        ? "border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed"
                                        : currentDirector
                                          ? "border-gray-200 hover:border-primary-300 hover:bg-primary-50"
                                          : "border-amber-200 bg-amber-50/50 hover:border-primary-300 hover:bg-primary-50",
                                    )}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium text-gray-900 text-sm truncate">
                                          {club.name}
                                        </p>
                                        {club.clubSections.map((s: string) => (
                                          <SectionBadge
                                            key={s}
                                            section={s as ClubType}
                                            size="xs"
                                            className="shrink-0"
                                          />
                                        ))}
                                      </div>
                                      {alreadyAssigned ? (
                                        <p className="text-xs text-orange-500 mt-0.5">
                                          Ya tiene este rol asignado
                                        </p>
                                      ) : currentDirector ? (
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          Director:{" "}
                                          <span className="font-medium">
                                            {currentDirector.name}
                                          </span>
                                        </p>
                                      ) : (
                                        <p className="text-xs text-amber-600 font-medium mt-0.5">
                                          Sin director asignado
                                        </p>
                                      )}
                                    </div>
                                    {assignRoleMutation.isPending ? (
                                      <Loader2 className="w-4 h-4 animate-spin text-primary-600 shrink-0" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                /* Union / Association scope selection */
                <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                  <p className="text-sm text-gray-600">
                    Selecciona{" "}
                    {effectiveRole === "admin_union"
                      ? "la unión"
                      : "la asociación"}
                    :
                  </p>
                  {scopeOptions.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">
                      No hay{" "}
                      {effectiveRole === "admin_union"
                        ? "uniones"
                        : "asociaciones"}{" "}
                      disponibles
                    </p>
                  ) : (
                    scopeOptions.map((option) => {
                      const alreadyAssigned =
                        selectedUser &&
                        effectiveRole &&
                        hasExistingRole(
                          selectedUser.id,
                          effectiveRole,
                          option.id,
                        );
                      return (
                        <button
                          key={option.id}
                          onClick={() =>
                            !alreadyAssigned &&
                            handleSelectScope(
                              option.id,
                              option.name,
                              option.type,
                            )
                          }
                          disabled={
                            !!alreadyAssigned || assignRoleMutation.isPending
                          }
                          className={cn(
                            "w-full flex items-center gap-3 p-3 rounded-xl border transition-colors duration-200 text-left",
                            alreadyAssigned
                              ? "border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed"
                              : "border-gray-200 hover:border-primary-300 hover:bg-primary-50",
                          )}
                        >
                          <Building2 className="w-5 h-5 text-gray-400" />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {option.name}
                            </p>
                            {alreadyAssigned && (
                              <p className="text-xs text-orange-500">
                                Ya tiene este rol asignado
                              </p>
                            )}
                          </div>
                          {assignRoleMutation.isPending ? (
                            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          {step !== "user" ? (
            <Button variant="ghost" onClick={goBack}>
              Atrás
            </Button>
          ) : (
            <div />
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}
